import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import hearts from "../Assets/CONFETTI.png";

//  -- Concepts and Flow --
//
// Meta marketing relies on training Meta in order to determine what
// to serve users in the future
//  - on the profile of users who click and sign up so
//    it can learn who to target
//  - secondary, to target those who have signed up but
//    not subscribed
//  - third, target users/subscribers for other services
//
// Meta trains its delivery system on:
//  - Who signs up
//  - Who subscribes
//  - What those users look like
//
// When CAPI data is sent, it applies the pixel_id. Meta knows then
// what ad account and what marketing campaigns are associated.
//
// 'Signup and subscription are not tied back to the original
//  ad click in your setup; they are matched to people and
//  then statistically attributed to ads through Meta’s modeling,
//  not via fbc or fbclid.'
//
// This means that when we provide Meta, via CAPI, the email, etc
// of users who sign up, it will be able to tie this back to a
// marketing campaing and can then profile the user. Email will
// be supported with phone number, User Agent (UA - phone details)
// and IP among others and where possible.
//
// While Lead is used in some cases, typically with Pixel, in this
// scenario CAPI will send CompleteRegistration. This means they
// have downloaded, installed and signed up.
// Further CAPI events will be sent for instance for subscription.
// Lead means intent and could be used for instance following
// download but we don't have attribution data/deferred deep links
// and is this step any more useful than knowing about users who
// actually registered.
//
// Ad clicks themselves are recognised directly by Meta
// There's no need to use pixel here, it's are only a
// redirect page and no need to send back to Meta via CAPI
//
// Using pixel in redirect modules is not standard and not
// necessary. There are no actions worth informing Meta about
// and they essentially have the click info from the click
// itself. It would also mean having to agree cookies and GDPR
// implications. Using Lead in such a case can also cause double
// counts, skewing stats
//
// Optimal is to be able to tie an email with and fbc from ad
// click directly. Relying on email after user subscribes allows
// Meta to probabalistically match the user to an ad click.
// An internal mechanism that performs something similar, as has
// been implemented previously in /app, can 'Pollute the training
// signals, Mislead lookalike modeling, Cause wrong cost-per-action
// calculations, Lead to suboptimal ad delivery'
// While it has been effectively used, it will now be bypassed.
//
// Summary:
//  - attempt to redirect to the appropriate app store. Store
//    pixel data locally (app's db) as has been done throughout
//    but no longer send also to CAPI
//  - if cannot direct to app store, e.g. on desktop then direct
//    to landing page to allow click through
//  - later, when a user signs up, the email and other identifiers
//    (but not fbc ad related info) are sent via CAPI for Meta
//    matching
//  - note that there are tags applied to tracking data, not only
//    for this redirect, but also for download (once app opened)
//    and registration, both of which attempt matches on the id
//
// Note that we could append the uuid and click data to the uri if
// being redirected to the landing page rather than app store but
// there seems little benefit in further tracking action there
// If landing there directly no tracking is done since this is only
// for Meta purposes
// For deferred deep links need appflyer or the like and then could
// say append the tuuid and it would arrive in the app. Android
// does support a one-time referrer (=tuuid) but no ios equiv.
// As described above, current method relies on Meta matching
// so no need to pass through ids to tie back to ad


export default function AppRedirect() {
  const location = useLocation()

  // In previous version trackinguuid in /app page was stored in
  // cookie and checked (also intended for attribution see below).
  // Continue generating and using this trackingUuid because it
  // may still be useful in future for the app download but do not
  // store it in cookies and it is no longer useful in determining
  // multiple visits for this user
  const tuuid = uuidv4()

  const registerSystemUsageStat = async (statName) => {
    const url = `${process.env.REACT_APP_API}/system/register-system-usage-stat`
    const payload = { statName }

    // fire and forget
    // Use fetch rather than axios to avoid issues with latency,
    // mobile browser possibly killing page regardless, redirect
    // happening before operation completes etc.
    // Using keepalive means no interuption to request when page
    // unloaded
    // Consider using navigator.sendBeacon
    fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      keepalive: true,
      headers: { "Content-Type": "application/json" },
    })
  }

  const storeTrackingData = (userAgent) => {

    const urlParams = new URLSearchParams(location.search);
    const fbclid = urlParams.get('fbclid')

    // fbc is not available from a cookie without pixel, derive from fbclid
    // from uri this represents the ad click so is essential
    const fbc = fbclid
      ? `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`
      : null

    // fbp is not available from a cookie without pixel but it only represents
    // a unique browser sessions so generating one here is sufficient. Note that
    // the separate uuid was originally generated in expectation of sending to
    // the app store as deferred deeplink data (storing to server and tying in
    // on return) but this cannot be done (data is lost going to app store, would
    // only be present if going direct to the app already installed. This type of
    // attribution data from deferred deep links requires a paid service). The
    // uuid is maintained server side schema for consistency still however.
    const fbp = tuuid

    const utm_source = urlParams.get('utm_source')
    const utm_id = urlParams.get('utm_id')
    const utm_medium = urlParams.get('utm_medium')
    const utm_campaign = urlParams.get('utm_campaign')
    const utm_content = urlParams.get('utm_content')
    const utm_term = urlParams.get('utm_term')

    const eventData = {
      eventName: 'Redirect',
      pixelData: {
        fbp,
        fbc,
      }
    }

    const eventSubCategory = 'AppStoresRedirectPage'

    const customData = {
      eventSubCategory: eventSubCategory,
      utmParams: {
        utm_source,
        utm_id,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
      }
    }

    const trackingData = {
      trackingUuid: tuuid,
      //deviceFingerprint
      deviceSpec: userAgent,
      fbp,
      fbc,
      fbclid,
      utmParams: customData.utmParams,
      arrivalUrl: '/redirect',
      //deeplink
      //events: {
    }

    const url = `${process.env.REACT_APP_API}/users/log-browser-tracking-info`
    const payload = { eventData, customData, trackingData }

    // fire and forget
    // This information is anonymous and therefore ok to store
    // Use fetch rather than axios to avoid issues with latency,
    // mobile browser possibly killing page regardless, redirect
    // happening before operation completes etc.
    // Using keepalive means no interuption to request when page
    // unloaded
    fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      keepalive: true,
      headers: { "Content-Type": "application/json" },
    })
  }

  useEffect(() => {
    // --- Store-only redirect (no deep link) + safe fallback ---
    if (typeof window === "undefined") return

    const userAgent = navigator.userAgent || "Unknown"

    // check the search length for params (which suggest it came from an ad.
    // Could also be explicit and check say fbcid but is sufficient for now)
    // The pathname here should be something like /redirect?params=
    if (location.search.length > 1) {
      registerSystemUsageStat("AppRedirectPageHit")
      storeTrackingData(userAgent)
    }

    const isAndroid = /android/i.test(userAgent);

    // iPadOS can present as "Macintosh" but with touch points
    const isIOS =
      /iPad|iPhone|iPod/i.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    const androidStoreLink =
      process.env.REACT_APP_ANDROID_STORE_LINK ||
      "https://play.google.com/store/apps/details?id=com.firstdance.firstdance";

    const iosStoreLink =
      process.env.REACT_APP_IOS_STORE_LINK ||
      "https://apps.apple.com/uk/app/first-dance/id6739738754";

    const fallback =
      process.env.REACT_APP_REDIRECT_FALLBACK || "https://firstdance.app";

    const storeLink = isAndroid ? androidStoreLink : isIOS ? iosStoreLink : fallback;

    // If navigation succeeds (store opens), the document typically becomes hidden.
    let didHide = false;
    const onVisibilityChange = () => {
      if (document.hidden) didHide = true;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Short delay for server requests to kick off, but keep it snappy.
    const goTimer = window.setTimeout(() => {
      window.location.assign(storeLink)
    }, 150)

    // Safety net: if store navigation is blocked, take them to your website.
    // (You can tune the time; 2000–3000ms is common.)
    const fallbackTimer = window.setTimeout(() => {
      if (!didHide) window.location.assign(fallback);
    }, 2500);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearTimeout(goTimer);
      window.clearTimeout(fallbackTimer);
    };
  }, []);


  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <img
        src={hearts}
        alt=""
        style={{ position: "absolute", top: 50, width: "100vw" }}
      />
      <p style={{ fontWeight: 700, color: "#1b1b1b" }}>
        Taking you to the app store…
      </p>
    </div>
  );
}
